import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Question, X, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

/**
 * Contextual help popover. Place next to any page header.
 *
 * Usage:
 *   <HelpHint topic="How the Wealth Health Score™ works" query="wealth health score" />
 *
 * Fetches the most-relevant user-guide chunk on click and shows it in a
 * branded popover with a link to the full Getting Started page.
 */
const HelpHint = ({ topic, query, testid = 'help-hint' }) => {
  const [open, setOpen] = useState(false);
  const [chunk, setChunk] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !chunk) {
      setLoading(true);
      api.get(`/user-guide/search?q=${encodeURIComponent(query)}&limit=1`)
        .then((res) => {
          setChunk(res.data.results?.[0] || null);
        })
        .catch(() => setChunk(null))
        .finally(() => setLoading(false));
    }
  }, [open, query, chunk]);

  return (
    <span className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        data-testid={testid}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 text-[#4A5568] hover:bg-[#C5A059]/10 hover:text-[#0A1128] hover:border-[#C5A059] transition-all"
        aria-label="Show help"
      >
        <Question size={16} weight="duotone" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[90]"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 top-10 z-[91] w-80 sm:w-96 bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
            data-testid={`${testid}-popover`}
          >
            <div className="bg-[#0A1128] px-4 py-3 flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-[#C5A059] font-semibold">Quick Help</p>
                <p className="text-sm font-medium text-white mt-0.5">{topic}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid={`${testid}-close`}
                className="text-white/70 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              {loading ? (
                <p className="text-sm text-[#A0AABF]">Loading…</p>
              ) : chunk ? (
                <p className="text-sm text-[#1A202C] leading-relaxed whitespace-pre-line">{chunk.content}</p>
              ) : (
                <p className="text-sm text-[#4A5568]">No quick help available — open the full guide for details.</p>
              )}
              <Link
                to="/getting-started"
                onClick={() => setOpen(false)}
                data-testid={`${testid}-open-guide`}
                className="inline-flex items-center gap-1 mt-4 text-xs text-[#0A1128] font-semibold uppercase tracking-[0.08em]"
              >
                Open full Getting Started guide <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </>
      )}
    </span>
  );
};

export default HelpHint;
